// REACT
import { useMemo, useState } from "react";

// MATERIAL UI
import { ButtonBase, Skeleton, Stack, Typography } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

// REDUX
import api from "src/store/features/api";

// STYLES & UTILS
import { BORDER_LIGHT, Card, NAVY, SALMON, TEXT_SECONDARY } from "./styles";
import { formatUtcDate, sortReleasedDocumentsByDate } from "./utils";

const MAX_DOCUMENTS = 4;

const formatDocumentType = (type?: string) => {
  if (!type) return "Tax form";
  if (type === "5498") return "Form 5498";
  return `Form ${type.replaceAll("_", "-")}`;
};

const TaxDocumentsWidget = () => {
  const [downloadError, setDownloadError] = useState("");
  const [downloadingId, setDownloadingId] = useState("");

  const documentsQuery = api.useGetReleasedTaxDocumentsQuery({});

  const documents = useMemo(() => {
    const uniqueById = new Map((documentsQuery.data?.data ?? []).map((item) => [item.id, item]));
    return sortReleasedDocumentsByDate(Array.from(uniqueById.values())).slice(0, MAX_DOCUMENTS);
  }, [documentsQuery.data]);

  // Each item's `url` is a presigned TaxBit link that can expire before the user gets around to
  // clicking Download, so refetch immediately beforehand rather than opening whatever URL
  // happened to load with the page. The tab is opened synchronously inside the click gesture
  // (popup blockers reject window.open after an await) and navigated once the fresh URL arrives.
  const handleDownload = async (item: (typeof documents)[number]) => {
    setDownloadError("");
    setDownloadingId(item.id);
    const tab = window.open("", "_blank");
    if (tab) tab.opener = null;
    try {
      const fresh = await documentsQuery.refetch().unwrap();
      const freshUrl = fresh.data.find((doc) => doc.id === item.id)?.url;
      if (!freshUrl) {
        tab?.close();
        setDownloadError("Document is not ready yet.");
        return;
      }
      if (tab) tab.location.href = freshUrl;
      else window.open(freshUrl, "_blank", "noopener,noreferrer");
    } catch {
      tab?.close();
      setDownloadError("Unable to download document right now.");
    } finally {
      setDownloadingId("");
    }
  };

  return (
    <Card sx={{ p: 3, display: "flex", flexDirection: "column", gap: 1.75, height: "100%" }}>
      <Typography sx={{ fontSize: 17, fontWeight: 600, color: NAVY, lineHeight: 1.3 }}>
        Documents
      </Typography>
      <Typography sx={{ fontSize: 14, color: TEXT_SECONDARY, lineHeight: 1.55 }}>
        Tax forms and certification PDFs, ready to download as they're issued.
      </Typography>

      <Stack spacing={1.25} sx={{ mt: "auto", pt: 1.75, borderTop: `1px solid ${BORDER_LIGHT}` }}>
        {documentsQuery.isLoading &&
          Array.from({ length: 2 }).map((_, idx) => (
            <Skeleton key={`doc-skeleton-${idx}`} variant="text" height={22} />
          ))}

        {!documentsQuery.isLoading &&
          documents.map((item) => (
            <Stack
              key={item.id}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={1.5}>
              <Stack direction="row" alignItems="center" gap={1.25} sx={{ minWidth: 0 }}>
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    backgroundColor: BORDER_LIGHT,
                    flex: "none",
                  }}>
                  <DescriptionOutlinedIcon sx={{ fontSize: 16, color: NAVY }} />
                </Stack>
                <Typography sx={{ fontSize: 13, color: NAVY }} noWrap>
                  {`${formatDocumentType(item.type)} · ${item.year}`}
                  <Typography component="span" sx={{ fontSize: 13, color: TEXT_SECONDARY }}>
                    {` · ${formatUtcDate(item.created_date)}`}
                  </Typography>
                </Typography>
              </Stack>
              <ButtonBase
                onClick={() => handleDownload(item)}
                disabled={downloadingId === item.id}
                aria-label={`Download ${formatDocumentType(item.type)} ${item.year}`}
                sx={{
                  fontFamily: "Roobert, sans-serif",
                  fontSize: 13,
                  fontWeight: 500,
                  color: SALMON,
                  flex: "none",
                  "&:hover": { color: NAVY },
                  "&.Mui-disabled": { color: TEXT_SECONDARY },
                }}>
                {downloadingId === item.id ? "Downloading…" : "Download"}
              </ButtonBase>
            </Stack>
          ))}

        {!documentsQuery.isLoading && documents.length === 0 && !documentsQuery.error && (
          <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY }}>
            No tax documents available yet — your 1099-DA will appear here when it's issued.
          </Typography>
        )}

        {!!documentsQuery.error && (
          <Typography sx={{ fontSize: 12, color: "error.main" }}>
            Unable to load tax documents right now.
          </Typography>
        )}

        {!!downloadError && (
          <Typography sx={{ fontSize: 12, color: "error.main" }}>{downloadError}</Typography>
        )}
      </Stack>
    </Card>
  );
};

export default TaxDocumentsWidget;
